import React, { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useGetAdminPlatformSettingsQuery,
  useUpdateMessagingProvidersMutation,
} from "../../../store/adminPlatformSettingsApi";

export default function MessagingProvidersSettings() {
  const { data, isLoading, isError, error, isFetching } =
    useGetAdminPlatformSettingsQuery();
  const [updateProviders, { isLoading: isSaving }] =
    useUpdateMessagingProvidersMutation();

  const [smsProvider, setSmsProvider] = useState("");
  const [whatsappProvider, setWhatsappProvider] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!data) return;
    if (data.sms_provider) setSmsProvider(String(data.sms_provider));
    if (data.whatsapp_provider) {
      const wa = String(data.whatsapp_provider);
      // FlyMySMS / eBulkSMS are SMS-only; never keep them selected for WhatsApp
      setWhatsappProvider(
        wa === "flymysms" || wa === "ebulksms" ? "sendchamp" : wa
      );
    }
  }, [data]);

  const catalog = Array.isArray(data?.messaging_providers)
    ? data.messaging_providers
    : [];

  const smsCatalog = Array.isArray(data?.sms_providers)
    ? data.sms_providers
    : catalog;

  const whatsappCatalog = Array.isArray(data?.whatsapp_providers)
    ? data.whatsapp_providers
    : catalog.filter(
        (item) => item?.id && item.id !== "flymysms" && item.id !== "ebulksms"
      );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!smsProvider || !whatsappProvider) {
      toast.error("Select both SMS and WhatsApp providers.");
      return;
    }
    if (!password.trim()) {
      toast.error("Enter your password to confirm this change.");
      return;
    }
    try {
      await updateProviders({
        sms_provider: smsProvider,
        whatsapp_provider: whatsappProvider,
        password,
      }).unwrap();
      toast.success("Messaging providers updated.");
      setPassword("");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Update failed.");
    }
  };

  const loadErrorMessage =
    isError &&
    (error?.data?.message ||
      error?.message ||
      "Could not load platform settings.");

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-xl p-4 mb-6 flex items-center">
        <Link to="/sms-messaging" aria-label="Back to SMS messaging">
          <BsArrowLeft className="text-xl text-gray-600 mr-4 cursor-pointer" />
        </Link>
        <h1 className="text-2xl font-bold text-[#45464e]">
          Messaging providers
        </h1>
      </div>

      {loadErrorMessage && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 text-red-600 text-sm">
          {loadErrorMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6 max-w-xl">
        {isLoading && !data ? (
          <p className="text-gray-600 text-sm">Loading settings…</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-gray-600 text-sm">
              Choose the active SMS and WhatsApp gateways separately. FlyMySMS
              and eBulkSMS are SMS-only. Confirm with your Uniquecoop admin
              password.
            </p>

            <div>
              <label
                htmlFor="sms_provider"
                className="block text-sm font-medium text-[#45464e] mb-1"
              >
                SMS provider
              </label>
              <select
                id="sms_provider"
                value={smsProvider}
                onChange={(ev) => setSmsProvider(ev.target.value)}
                disabled={smsCatalog.length === 0 || isFetching}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 focus:ring-0 outline-none bg-white"
              >
                {smsCatalog.length === 0 ? (
                  <option value="">No options</option>
                ) : (
                  smsCatalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label
                htmlFor="whatsapp_provider"
                className="block text-sm font-medium text-[#45464e] mb-1"
              >
                WhatsApp provider
              </label>
              <select
                id="whatsapp_provider"
                value={whatsappProvider}
                onChange={(ev) => setWhatsappProvider(ev.target.value)}
                disabled={whatsappCatalog.length === 0 || isFetching}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 focus:ring-0 outline-none bg-white"
              >
                {whatsappCatalog.length === 0 ? (
                  <option value="">No options</option>
                ) : (
                  whatsappCatalog.map((item) => (
                    <option key={`wa-${item.id}`} value={item.id}>
                      {item.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label
                htmlFor="admin_password"
                className="block text-sm font-medium text-[#45464e] mb-1"
              >
                Your password
              </label>
              <input
                id="admin_password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 focus:ring-0 outline-none bg-[#fafafa]"
                placeholder="Required to save changes"
              />
            </div>

            <button
              type="submit"
              disabled={
                isSaving ||
                smsCatalog.length === 0 ||
                whatsappCatalog.length === 0 ||
                !!loadErrorMessage
              }
              className="bg-[#8b313a] text-white py-2 px-6 rounded-md text-sm font-medium hover:bg-[#6b252a] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving…" : "Save providers"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
